/* eslint-disable no-console */
import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { updateJobServiceTransition, bookInFlaggedJob } from "requesters/jobServiceRequester"
import { getFlagTypeOptionsByJobService, postFlag } from "requesters/flagRequester"
import { Modal } from "react-bootstrap"
import SweetAlert from "react-bootstrap-sweetalert"
import { getBaseUrl } from "../../../reducers"
import Dropzone from "../../../../../components/utils/Dropzone"
import {
  MAX_SIZE_UPLOADED_FILE,
  MAX_SIZE_UPLOADED_FILE_TEXT,
  PRODUCT_CODE_PASS_THE_RESOLVE_FLAGGING_STEP
} from "../../../../../utils/constants"
import { capitalizeString, camelToSnake } from "../../../../../utils"
import CustomCheckItem from "../../../../../components/CustomCheckItem"

const FlagJobServiceModal = ({
 show, onHide, jobService, reloadData
}) => {
  const baseUrl = useSelector(getBaseUrl)
  const { id, attributes: { state, code, existingFilesCanBeFlagged } } = jobService
  const haveExistingFilesCanBeFlagged = !!existingFilesCanBeFlagged && Object.keys(existingFilesCanBeFlagged).length > 0 && Object.values(existingFilesCanBeFlagged).flat().length > 0

  const [flagTypeOptions, setFlagTypeOptions] = useState({})
  const [sendToOptions, setSendToOptions] = useState(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [largeUploads, setLargeUploads] = useState([])
  const [selectedSendTo, setSelectedSendTo] = useState(null)
  const [selectedFlagType, setSelectedFlagType] = useState(null)
  const [currentFlagTypeOptions, setCurrentFlagTypeOptions] = useState([])
  const [flagNotes, setFlagNotes] = useState("")
  const [showError, setShowError] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)
  const [selectedFilesToBeFlagged, setSelectedFilesToBeFlagged] = useState([])

  const fetchFlagTypes = () => {
    getFlagTypeOptionsByJobService(baseUrl, id)
    .then(res => {
      if (res.status === 200) {
        const [firstKey] = Object.keys(res.response);
        const defaultSelectedFlagType = firstKey ? res.response[firstKey][0]?.value : null
        setCurrentFlagTypeOptions(res.response[firstKey]);
        setSendToOptions(Object.keys(res.response));
        setFlagTypeOptions(res.response);
        setSelectedFlagType(defaultSelectedFlagType)
        setSelectedSendTo(firstKey)
      }
    })
    .catch(err => console.log(err))
  }

  useEffect(() => {
    fetchFlagTypes()
  }, [])

  const handleChangeSelectedSendTo = value => {
    setSelectedSendTo(value)
    setCurrentFlagTypeOptions(flagTypeOptions[value])
  }

  const handlePreview = e => {
    const files = e.value
    const largeFiles = files?.filter(file => file.size > MAX_SIZE_UPLOADED_FILE)
    if (largeFiles?.length) {
      setLargeUploads(largeFiles)
      setUploadedFiles([])
      setShowError(true)
    } else {
      setUploadedFiles([...uploadedFiles, ...files])
    }
  }

  const handleDeleteFile = fileToDelete => {
    const updatedFiles = uploadedFiles.filter(file => file !== fileToDelete);
    setUploadedFiles(updatedFiles);
  }

  const handleBookInFlaggedJob = () => {
    bookInFlaggedJob(baseUrl, id)
      .then(res => {
        const jobServiceId = Object.keys(res?.response?.jobService)[0];
        window.location.href = `/jobs/${jobService.attributes.jobId}/job_services/${jobServiceId}`
    })
    // eslint-disable-next-line no-console
    .catch(err => console.log(err))
  }

  const handleSubmit = () => {
    if (flagNotes) {
      const data = new FormData()
      data.append("job_service_id", id)
      data.append("flag_type", selectedFlagType)
      data.append("sent_to", selectedSendTo)
      data.append("notes", flagNotes)
      data.append("from_at", state)
      uploadedFiles.forEach(file => data.append(`uploaded_files[]`, file))
      selectedFilesToBeFlagged.forEach(fileId => data.append(`selected_existing_file_ids[]`, fileId))

      setDisableSubmit(true)
      postFlag(baseUrl, data)
        .then(res => {
          if (res.status === 201) {
            updateJobServiceTransition(baseUrl, id, "require-additional-info", `Flagged Added: ${flagNotes}`)
            .then(res => {
              if (res.status === 200) {
                window.location.reload()
              }

              // Create Book in Job immediately and skip the Resolve Flag Step
              if (PRODUCT_CODE_PASS_THE_RESOLVE_FLAGGING_STEP.includes(code) && selectedFlagType === "PLAN-REQ") {
                handleBookInFlaggedJob()
              }
            })
            .catch(err => console.log(err))
          }
        })
        .catch(err => console.log(err))
    }
  }

  useEffect(() => {
    setLargeUploads([])
    if (flagNotes) {
      setDisableSubmit(false)
    } else {
      setDisableSubmit(true)
    }
  }, [flagNotes, show])

  const onChangeSelectedFiles = (e, elementID, fileName) => {
    const checkboxChecked = e.target.checked
    const uploadedFileID = elementID.split("-")?.pop()
    const isSelectedToBeFlagged = selectedFilesToBeFlagged.includes(uploadedFileID)

    if (checkboxChecked && !isSelectedToBeFlagged) {
      setSelectedFilesToBeFlagged([...selectedFilesToBeFlagged, uploadedFileID])
    } else if (!checkboxChecked && isSelectedToBeFlagged) {
      const newSelectedFiles = selectedFilesToBeFlagged.filter(selectedId => selectedId !== uploadedFileID)
      setSelectedFilesToBeFlagged(newSelectedFiles)
    }
  }

  const departmentExistingFilesElement = file => {
    const {
      data: { id, attributes: { fileName, documentCategory } }
    } = file
    const isFileWillUseForFlag = selectedFilesToBeFlagged.includes(id)

    return (
      <div key={id} className={`row no-gutters mb-1 py-2 ${isFileWillUseForFlag ? "hightlighted hl-warning" : ""}`}>
        <div className="col-6 text-left">
          <i className="bi bi-file-earmark mr-2"></i>
          {fileName}
        </div>
        <div className="col-3 text-center">
          {documentCategory}
        </div>
        <div className="col-3 text-right">
          <CustomCheckItem
            id={`existing-department-file-${id}`}
            name={fileName}
            label="Use to be Flagged"
            iconClasses="font-085rem"
            checked={isFileWillUseForFlag}
            setChecked={onChangeSelectedFiles} />
        </div>
      </div>
    )
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <div className="modal-header px-md-5 pt-4 border-0">
        <h2 className="modal-title">ADD FLAG</h2>
      </div>
      <div className="modal-body px-md-5">
        <form>
          <div className="form-group row mb-3">
            <label className="col-4">Send to</label>
            <div className="col-6">
              <select className="form-control" value={selectedSendTo} onChange={e => handleChangeSelectedSendTo(e.target.value)}>
                {sendToOptions && sendToOptions.map(team => (
                  <option value={team} key={team}>{capitalizeString(team)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group row mb-5">
            <label className="col-4">Flag type</label>
            <div className="col-6">
              <select className="form-control" value={selectedFlagType} onChange={e => setSelectedFlagType(e.target.value)}>
                {currentFlagTypeOptions?.map(flagType => (
                  <option value={flagType.value} key={flagType.value}>{flagType.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group row border-bottom pb-4">
            <div className={`col-12 mb-${haveExistingFilesCanBeFlagged ? "2" : "3"}`}>
              <textarea
                name="flag_notes"
                className="form-control"
                placeholder="Notes*"
                rows="4"
                value={flagNotes}
                onChange={e => setFlagNotes(e.target.value)}>
              </textarea>
            </div>
            <div className="col-12 mb-0">
              { haveExistingFilesCanBeFlagged ? (
                <div className="mb-2">
                  <p className="mb-0"><strong className="text-uppercase">Current Department Files</strong></p>
                  <div className="ml-0 mt-1 bright-light-gray-block">
                    { Object.keys(existingFilesCanBeFlagged).map(devisionKey => (
                      <div key={devisionKey} className="row no-gutters mb-0 py-2">
                        <div className="col-12">
                          <b>{capitalizeString(camelToSnake(devisionKey))}</b>
                          { Object.values(existingFilesCanBeFlagged[devisionKey]).map(file => departmentExistingFilesElement(file)) }
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mb-2">
                  No existing files can be select to flag!
                </p>
              )}
            </div>
            <div className="col-12 mb-5">
              <Dropzone
                wrapperClass="drag-drop"
                multiple
                name="photo"
                setUploadedFiles={setUploadedFiles}
                handleFileUpload={handlePreview} />
            </div>
            <div className="col-12">
              {!!uploadedFiles?.length && uploadedFiles.map(file => (
                <div key={file.path} className="file-list-item d-flex align-items-center justify-content-between">
                  <div>
                    <i className="bi bi-file-earmark mr-2"></i>
                    {file.path}
                  </div>
                  <i
                    className="bi bi-x-circle-fill text-danger delete-file"
                    onClick={() => handleDeleteFile(file)}
                    title="Delete File" />
                </div>
              ))}
            </div>
          </div>
        </form>
        <div className="modal-footer d-flex justify-content-center px-0 py-4">
          <button
            className="btn btn-red btn-lg"
            onClick={handleSubmit}
            disabled={disableSubmit}
            type="submit">
            Confirm Flag
          </button>
        </div>
      </div>

      <SweetAlert
        show={showError}
        type="error"
        title="File size exceeded!"
        style={{ width: "600px" }}
        closeOnClickOutside
        allowEscape={false}
        onConfirm={() => {
          setShowError(false)
          setLargeUploads([])
          }}>
        Max size is {MAX_SIZE_UPLOADED_FILE_TEXT}
      </SweetAlert>
    </Modal>
  )
  }
export default FlagJobServiceModal
